// IsomorphicService — the (lot_number, lot_number_suffix) pair helpers (BL-1200).
//
// BL-1200 retired auction_lots.sort_order: lot_number is an INTEGER and the
// A–Z positional-insert suffix lives in its own lot_number_suffix column
// ('' = no suffix). These two pure helpers are the single seam every surface
// shares so display labels and ordering can never drift:
//
//   • `formatLotNumber` — the composed display label ("12A"). Used by admin
//     display surfaces, bidder mappers, PDFs, notification placeholders, and
//     the invoice/settlement snapshot writers (which freeze the label as TEXT
//     at document creation).
//   • `compareLotNumberPairs` — client-side ordering identical to the native
//     SQL `ORDER BY lot_number ASC NULLS LAST, lot_number_suffix ASC`:
//     integers numerically, then suffixes lexicographically with '' first
//     (2 < 10 < 10A < 10AA < 10B < 11; A < AA < AB < B per the ADR 036 brief).
//
// Read-only and pure: no I/O, no writes, deterministic. Safe in a useMemo.

/** The minimum pair shape both helpers need. Server rows and client DTOs both satisfy it. */
export interface LotNumberPair {
  /** The integer lot number, or null for an unnumbered lot. */
  lotNumber: number | null;
  /** The A–Z insert suffix ('' = none, ≤2 chars). */
  lotNumberSuffix: string;
}

/**
 * Compose the pair into the display label ("12", "12A"), or null when the
 * lot is unnumbered. Lot number 0 is formatted, not swallowed — `0` is valid.
 *
 * @param pair The lot's (lotNumber, lotNumberSuffix) pair.
 * @returns The composed label, or null for an unnumbered lot.
 * @sideEffect None — pure function.
 */
export function formatLotNumber(pair: LotNumberPair): string | null {
  if (pair.lotNumber == null) return null;
  return `${pair.lotNumber}${pair.lotNumberSuffix ?? ''}`;
}

/**
 * The placeholder a VISIBLE lot shows in place of a number it does not have.
 *
 * The em dash is the repo-wide "this value is absent" glyph — the admin
 * Ordering tab already renders exactly this for an unnumbered lot
 * (`LotNumberCell`: `{displayNumber ?? '—'}`), as do the clerk console's lot
 * rows and the post-auction tables. Reusing it keeps one answer to "what does a
 * missing lot number look like" across both apps rather than inventing a
 * bidder-only "TBD".
 */
export const UNNUMBERED_LOT_NUMBER = '—';

/**
 * Compose the display label for a lot that is ALREADY VISIBLE to a bidder,
 * falling back to {@link UNNUMBERED_LOT_NUMBER} when it carries no number.
 *
 * BL-1167 deleted the catalog Publish lifecycle on the promise that a number is
 * a position (BL-1163), so a visible lot always has one. The database can still
 * produce the state that promise excludes — readied, non-deleted `auction_lots`
 * rows with `lot_number IS NULL` — and AC 14 required a defensive fallback for
 * exactly that: "a null/0 number must never silently hide the badge." Every
 * bidder display guard (`isAssignedLotNumber` on the lot detail page,
 * `hasLotNumber` on the card) reads a `0`/null as "render nothing", so the
 * fallback has to happen at the mapping seam, above those guards.
 *
 * Use this ONLY where visibility has already been decided (the caller is
 * mapping a lot the bidder can see). Anywhere the absence of a number is itself
 * the thing being tested — ordering, admin editors, the `0` sentinel's meaning
 * — keep using {@link formatLotNumber}, which still answers `null`.
 *
 * @param pair The lot's (lotNumber, lotNumberSuffix) pair.
 * @returns The composed label ("12", "12A"), or the unnumbered placeholder.
 * @sideEffect None — pure function.
 */
export function formatVisibleLotNumber(pair: LotNumberPair): string {
  return formatLotNumber(pair) ?? UNNUMBERED_LOT_NUMBER;
}

/**
 * Compare two pairs in catalog order — the client-side mirror of the native
 * SQL `ORDER BY lot_number ASC NULLS LAST, lot_number_suffix ASC`.
 *
 * Integer first (numeric, so 2 < 10 — the lexicographic bug class BL-1200
 * kills), then the suffix by plain code-point comparison, which gives exactly
 * the confirmed scheme: '' < 'A' < 'AA' < 'AB' < 'B'. Unnumbered lots
 * (lotNumber null) sort last, mirroring NULLS LAST.
 *
 * @returns Negative when `a` precedes `b`, positive when it follows, 0 when equal.
 * @sideEffect None — pure function.
 */
export function compareLotNumberPairs(a: LotNumberPair, b: LotNumberPair): number {
  const aNull = a.lotNumber == null;
  const bNull = b.lotNumber == null;
  if (aNull !== bNull) return aNull ? 1 : -1; // NULLS LAST
  if (!aNull && a.lotNumber !== b.lotNumber) {
    return (a.lotNumber as number) - (b.lotNumber as number);
  }
  const as = a.lotNumberSuffix ?? '';
  const bs = b.lotNumberSuffix ?? '';
  return as < bs ? -1 : as > bs ? 1 : 0;
}

/** The composed-label shape: an integer run then an optional ≤2-char A–Z suffix. */
const LOT_NUMBER_LABEL = /^(\d+)([A-Z]{0,2})$/;

/**
 * Parse a composed display label ("10A") back into its (lotNumber, suffix)
 * pair — the exact inverse of {@link formatLotNumber}. This is what lets a
 * FROZEN TEXT snapshot column (`invoice_items.lot_number` / `settlement_items`,
 * which BL-1200 stores as the composed label) be ordered numerically with
 * {@link compareLotNumberPairs}, with no schema change and no join back to the
 * live lot (the sort key is derived from exactly the label being displayed).
 *
 * A label that isn't a valid lot number — a blank line, a manual free-text
 * invoice line ("Handling fee"), a bare suffix, or `null`/`undefined` — parses
 * to `{ lotNumber: null }` so `compareLotNumberPairs` sinks it to the end
 * (mirroring `NULLS LAST`) rather than sorting it lexically among the numbered
 * lots. Lot number 0 is a valid integer and is preserved (`0`, not swallowed).
 *
 * @param label The composed label, e.g. "10A", or null/undefined/free text.
 * @returns The parsed pair; `{ lotNumber: null, lotNumberSuffix: '' }` when the
 *   label is not a valid lot number.
 * @sideEffect None — pure function.
 */
export function parseLotNumberLabel(label: string | null | undefined): LotNumberPair {
  const match = label == null ? null : LOT_NUMBER_LABEL.exec(label.trim());
  if (!match) return { lotNumber: null, lotNumberSuffix: '' };
  return { lotNumber: Number(match[1]), lotNumberSuffix: match[2] };
}
